import { useState } from "react";
import { Option } from "@/pages/get-don/tasks.types.ts";
import IconGripHorizontal from "@/assets/icons/grip-horizontal.svg?react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";

interface Props {
  options: Option[];
  value: string;
  onChange: (ids: string) => void;
}

const SortableItem = ({
  item,
  isDragging,
  orderIndex,
}: {
  item: Option;
  isDragging: boolean;
  orderIndex: number;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isSorting } = useSortable({
    id: item.id,
    transition: {
      duration: 200,
      easing: "cubic-bezier(0.25, 1, 0.5, 1)",
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isSorting ? transition : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-px rounded-xl w-full ${isDragging ? "opacity-30" : ""}`}
    >
      <div className=" py-2.5 px-3 w-full rounded-xl bg-gray-4">
        <div className="flex items-start justify-between w-full gap-3">
          <div className="flex items-start min-w-0 gap-3">
            <span className="flex-center size-6 shrink-0 rounded-full bg-white/[0.08] text-xs font-semibold text-white/80">
              {orderIndex}
            </span>
            <p className="text-sm font-semibold text-white text-start leading-5">
              {item.title}
            </p>
          </div>
          <div
            className="flex items-center shrink-0 touch-none cursor-grab active:cursor-grabbing"
            {...attributes}
            {...listeners}
          >
            <IconGripHorizontal className="text-gray-400" />
          </div>
        </div>
      </div>
    </div>
  );
};

const DragItem = ({ item, orderIndex }: { item: Option; orderIndex: number }) => (
  <div className="p-px rounded-xl w-full bg-yellow-linear shadow-[0_7px_33px_-24px_rgba(235,161,50,0.24)] scale-[1.02]">
    <div className="py-2.5 px-3 w-full rounded-xl bg-gray-4">
      <div className="flex items-start justify-between w-full gap-3">
        <div className="flex items-start min-w-0 gap-3">
          <span className="flex-center size-6 shrink-0 rounded-full bg-white/[0.08] text-xs font-semibold text-white/80">
            {orderIndex}
          </span>
          <p className="text-sm font-semibold text-white text-start leading-5">
            {item.title}
          </p>
        </div>
        <div className="flex items-center shrink-0 p-1">
          <IconGripHorizontal className="text-gray-400" />
        </div>
      </div>
    </div>
  </div>
);

const Ordering = ({ options, value, onChange }: Props) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const currentOrder = value ? value.split(",") : options.map(opt => opt.id);

  const optionsMap = options.reduce((acc, opt) => {
    acc[opt.id] = opt;
    return acc;
  }, {} as Record<string, Option>);

  const orderedOptions = currentOrder.map(id => optionsMap[id]).filter(Boolean);
  const activeItem = activeId ? optionsMap[activeId] : null;
  const activeOrderIndex = activeId ? currentOrder.indexOf(activeId) + 1 : 0;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 3 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 80, tolerance: 5 } }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;

    const oldIndex = currentOrder.indexOf(String(active.id));
    const newIndex = currentOrder.indexOf(String(over.id));
    const newOrder = arrayMove(currentOrder, oldIndex, newIndex);
    onChange(newOrder.join(","));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={currentOrder} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col items-center w-full justify-center gap-2">
          {orderedOptions.map((item, index) => (
            <SortableItem
              key={item.id}
              item={item}
              orderIndex={index + 1}
              isDragging={activeId === item.id}
            />
          ))}
        </div>
      </SortableContext>

      {/* Drag overlay — smooth animated copy */}
      <DragOverlay dropAnimation={{
        duration: 200,
        easing: "cubic-bezier(0.25, 1, 0.5, 1)",
      }}>
        {activeItem ? (
          <DragItem item={activeItem} orderIndex={activeOrderIndex} />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default Ordering;
